import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationCenterProvider } from './context/NotificationCenterContext';
import { ThemeProvider } from './components/ui/ThemeToggle';
import { Toaster } from './components/ui/sonner';
import { ConfirmHost } from './lib/confirmAsync';
import { LoadingBar } from './components/LoadingBar';
import { PageLoader } from './components/ui/Loading';
import { GlobalConnectionStatus } from './components/GlobalConnectionStatus';
import { ThemePreferenceSync } from './components/ThemePreferenceSync';
import { initAnalytics } from './lib/analytics';
import { useNativePush } from './hooks/useNativePush';
import { useVersionCheck } from './hooks/useVersionCheck';
import './App.css';

// Lazy load pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const StaffDashboard = lazy(() => import('./pages/StaffDashboard'));
const VenueDashboard = lazy(() => import('./pages/VenueDashboard'));
const JobBoard = lazy(() => import('./pages/JobBoard'));
const JobDetails = lazy(() => import('./pages/JobDetails'));
const StaffProfile = lazy(() => import('./pages/StaffProfile'));
const VenueProfile = lazy(() => import('./pages/VenueProfile'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Messages = lazy(() => import('./pages/Messages'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const VenueBilling = lazy(() => import('./pages/VenueBilling'));
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'));
const VenuePlus = lazy(() => import('./pages/VenuePlus'));
const InterviewsPage = lazy(() => import('./pages/InterviewsPage'));
const VideoInterview = lazy(() => import('./pages/VideoInterview'));
const Settings = lazy(() => import('./pages/Settings'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const DataRightsPage = lazy(() => import('./pages/DataRightsPage'));
const TrustCenterPage = lazy(() => import('./pages/TrustCenterPage'));
const ShiftSwapPage = lazy(() => import('./pages/ShiftSwapPage'));
const StatusPage = lazy(() => import('./pages/StatusPage'));

const ProtectedRoute = ({ children, userType }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }

  if (user?.is_admin) {
    return <Navigate to="/admin" replace />;
  }

  if (userType && user?.user_type !== userType) {
    return <Navigate to={user?.user_type === 'staff' ? '/staff/dashboard' : '/venue/dashboard'} />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user?.is_admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { isAuthenticated, user } = useAuth();

  // No-op on web — fires only inside the iOS / Android Capacitor shell.
  useNativePush();

  // Polls /api/version every 5min; prompts a sticky toast with a Refresh
  // action when the live backend SHA drifts from this bundle's SHA. Only
  // fires for authenticated users (saves on backend hits for landing visits).
  useVersionCheck(isAuthenticated);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              user?.is_admin ? (
                <Navigate to="/admin" />
              ) : (
                <Navigate
                  to={user?.user_type === 'staff' ? '/staff/dashboard' : '/venue/dashboard'}
                />
              )
            ) : (
              <LandingPage />
            )
          }
        />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/privacy/data-rights" element={<DataRightsPage />} />
        <Route path="/trust" element={<TrustCenterPage />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/staff/dashboard"
          element={
            <ProtectedRoute userType="staff">
              <StaffDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venue/dashboard"
          element={
            <ProtectedRoute userType="venue">
              <VenueDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs"
          element={
            <ProtectedRoute>
              <JobBoard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/jobs/:jobId"
          element={
            <ProtectedRoute>
              <JobDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/:userId"
          element={
            <ProtectedRoute>
              <StaffProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venue/:userId"
          element={
            <ProtectedRoute>
              <VenueProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/marketplace"
          element={
            <ProtectedRoute>
              <Marketplace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages/:userId"
          element={
            <ProtectedRoute>
              <Messages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/interviews"
          element={
            <ProtectedRoute>
              <InterviewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/interview/:interviewId"
          element={
            <ProtectedRoute>
              <VideoInterview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/community"
          element={
            <ProtectedRoute>
              <CommunityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute userType="venue">
              <VenueBilling />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing/success"
          element={
            <ProtectedRoute userType="venue">
              <BillingSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/venue/plus"
          element={
            <ProtectedRoute userType="venue">
              <VenuePlus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts"
          element={
            <ProtectedRoute>
              <ShiftSwapPage />
            </ProtectedRoute>
          }
        />
        <Route path="/status" element={<StatusPage />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <NotificationCenterProvider>
            <ThemePreferenceSync />
            <LoadingBar />
            <div className="App noise-bg">
              <AppRoutes />
              <GlobalConnectionStatus />
              <Toaster />
              <ConfirmHost />
            </div>
          </NotificationCenterProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
